<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { RouterLink, useRoute, useRouter } from "vue-router";
import { useTheme } from "@/design/composables/use-theme";
import Badge from "@/design/primitives/Badge.vue";
import Button from "@/design/primitives/Button.vue";
import Check from "@/design/primitives/Check.vue";
import Icon from "@/design/primitives/Icon.vue";
import Popover from "@/design/primitives/Popover.vue";
import { signOut } from "@/features/auth/api/session";
import { useQueueAlert } from "@/features/inbox/api/use-queue-alert";
import { useProfile, useWorkspace } from "@/features/workspace/api/use-workspace";
import MyPresence from "@/features/workspace/components/MyPresence.vue";
import FileAvatar from "@/shared/files/FileAvatar.vue";
import { shellMessages } from "./i18n/shell";

/**
 * Quem eu sou, em qual workspace, e o que eu declaro sobre mim.
 *
 * ── POR QUE O CABEÇALHO DEIXOU DE DIZER "BENVOX V3" ───────────────────────
 *
 * Porque quem opera duas contas não sabia em qual estava antes de mandar a mensagem
 * errada. O nome do workspace já vinha no `GET /v3/workspace` e o nome e o e-mail da
 * pessoa no `GET /v3/auth/profile` — faltava lugar, não dado.
 *
 * ── POR QUE ISTO É UM ARQUIVO E NÃO MAIS UM BLOCO NO `App.vue` ────────────
 *
 * O shell estava em 216 linhas com o teto de componente em 200. Menu, presença e som
 * não cabiam lá — e nenhum deles é assunto do shell: são assuntos de
 * QUEM está logado, que é exatamente o que este arquivo desenha.
 *
 * `leave()` veio junto pelo mesmo motivo: o botão e a consequência dele no mesmo
 * arquivo evitam uma prop de callback atravessando o shell só para dizer "sair".
 */
const open = ref(false);

const { t, te } = useI18n({ useScope: "local", inheritLocale: true, messages: shellMessages });

const { data: profile } = useProfile();
const { data: workspace } = useWorkspace();
const alert = useQueueAlert();
const route = useRoute();
const { resolved, cycle } = useTheme();
const THEME_ICON = { light: "sun", dark: "moon" } as const;
const router = useRouter();

/**
 * Enquanto a consulta voa, o nome do produto — nunca o nome de um workspace.
 *
 * Um placeholder que se pareça com um nome de conta é a única coisa pior do que não ter
 * nome nenhum: quem opera duas contas leria o placeholder como resposta.
 */
const workspaceName = computed(() => workspace.value?.name ?? t("user.product"));

/**
 * O papel dito na língua de quem lê — e o código cru quando o servidor mandar um
 * que esta tela não conhece, que é mais honesto do que apagá-lo.
 */
const role = computed(() => {
	const code = profile.value?.role;
	if (!code) return "…";
	return te(`user.roles.${code}`) ? t(`user.roles.${code}`) : code;
});

// A MARCA do workspace saiu daqui: ela é a primeira linha do menu lateral agora
// (`WorkspaceChip`), e resolver o mesmo id de arquivo em dois cantos da mesma
// barra era pagar a consulta duas vezes para dizer a mesma coisa.

// Navegar fecha o menu: deixá-lo aberto sobre a página que a pessoa acabou de escolher é
// o mesmo incômodo do menu que não fecha no celular.
watch(route, () => {
	open.value = false;
});

// Clicar fora e Esc fecham — agora pelo `Popover`, que já os traz. Eram dois
// `useEventListener` à mão aqui, e este era o único dos sete painéis do app que os
// tinha; o que faltava não era comportamento, era o portal.

async function leave(): Promise<void> {
	open.value = false;
	await signOut();
	await router.push({ name: "sign-in" });
}
</script>

<template>
	<Popover
		:open="open"
		:label="t('user.trigger')"
		panel-class="max-h-[calc(100dvh-4rem)] w-80 overflow-y-auto rounded-2xl bg-surface-raised p-1.5 shadow-md"
		@update:open="open = $event"
	>
		<template #trigger>
			<!-- EU, e não a casa. O nome e a marca do workspace passaram a ser ditos na
			     primeira linha do menu lateral (`WorkspaceChip`), que é a divisão da V2:
			     onde estou à esquerda, quem sou à direita. Repeti-los aqui punha a mesma
			     resposta duas vezes na mesma barra — e o rosto, que era a única coisa
			     que só este canto pode dizer, não estava em lugar nenhum.
			     O nome do workspace continua DENTRO do painel aberto, onde não repete. -->
			<button
				type="button"
				class="flex h-10 min-w-0 items-center gap-2 rounded-full px-1.5 transition-colors hover:bg-bar-hover"
				:aria-label="t('user.trigger')"
			>
				<FileAvatar :name="profile?.name" :file-id="profile?.avatarFileId" size="sm" />
				<Badge tone="info" class="hidden sm:inline-flex">{{ role }}</Badge>
				<Icon name="chevronDown" class="hidden size-3 text-fg-subtle sm:block" />
			</button>
		</template>

		<header class="flex items-center gap-3 px-2.5 py-2.5">
			<!-- A inicial continua, como fallback de quem não subiu foto: uma silhueta
			     cinza diz menos do que a primeira letra de um nome. -->
			<FileAvatar :name="profile?.name" :file-id="profile?.avatarFileId" />
			<div class="min-w-0">
				<p class="truncate text-sm font-medium text-fg">{{ profile?.name ?? "…" }}</p>
				<p class="truncate text-xs text-fg-muted">{{ profile?.email ?? "" }}</p>
				<p class="truncate text-2xs text-fg-subtle">
					{{ t("user.inWorkspace", { name: workspaceName }) }}
				</p>
			</div>
		</header>

		<!-- Declarar-se ausente sem passar pela tela de Equipe, que um atendente não pode
		     abrir. O servidor sempre aceitou de qualquer sessão; faltava o lugar.
		     `empty:hidden` porque o controle não se desenha enquanto a presença voa, e
		     uma faixa com borda e nada dentro parece um bloco que falhou. -->
		<section class="rounded-lg px-2.5 py-2 empty:hidden">
			<MyPresence />
		</section>

		<section class="space-y-2 rounded-lg px-2.5 py-2 hover:bg-bg-muted">
			<Check
				:model-value="alert.enabled.value"
				:label="t('user.sound')"
				@update:model-value="alert.toggle()"
			/>
		</section>

		<!-- O tema no telefone: a barra de cima não tem lugar para ele (dono, 25/10). -->
		<button
			type="button"
			class="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-sm text-fg hover:bg-bg-muted md:hidden"
			@click="cycle"
		>
			<Icon :name="THEME_ICON[resolved]" class="size-4 text-fg-subtle" />
			{{ t("header.theme", { name: t(`header.themes.${resolved}`) }) }}
		</button>

		<div class="mt-1 flex items-center justify-between gap-2 px-2.5 py-2">
			<!-- A aba, e não `/account`: aquele endereço ainda vale, mas redireciona para
			     `?aba=workspace`, que é onde ficou o assunto DELE (nome do workspace, marca,
			     empresa). Este link chama-se "Sua conta" e tem de entregar a sua — nome, foto
			     e senha —, que desde a página única tem aba própria. -->
			<RouterLink
				to="/settings?aba=conta"
				class="text-xs text-accent hover:underline"
				@click="open = false"
			>
				{{ t("user.account") }}
			</RouterLink>
			<!-- Sem promessa de "em todos os dispositivos": `POST /v3/auth/sign-out` revoga
			     o token desta sessão e limpa o cookie deste navegador, e nada mais. -->
			<Button variant="secondary" size="sm" @click="leave">{{ t("user.signOut") }}</Button>
		</div>
	</Popover>
</template>
